// A private note on a thread ("waiting on Dad before I answer"), shown first when the thread comes
// back: on its card and on the thread page. Local only; never sent to Gmail or Claude.
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NotebookPen } from "lucide-react";
import type { ThreadNoteView } from "@shared/api";
import { cn } from "@web/lib/cn";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { saveThreadNote } from "./memoryOps";

const NOTE_MAX = 2000;

function NoteEditor({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial: string;
  busy: boolean;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  return (
    <div className="flex flex-col gap-2">
      <label className="sr-only" htmlFor="mm-thread-note">
        Private note
      </label>
      <textarea
        id="mm-thread-note"
        ref={ref}
        value={text}
        maxLength={NOTE_MAX}
        rows={2}
        placeholder="Where you left off, e.g. waiting on Dad before I answer"
        className="min-h-16 w-full resize-y rounded-lg border border-note-line bg-paper px-3 py-2 text-[15px] text-ink placeholder:text-faint"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSave(text);
          }
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="primary" disabled={busy} onClick={() => onSave(text)}>
          Save note
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <span className="text-xs text-muted">Only you see this. It never leaves MeeMail.</span>
      </div>
    </div>
  );
}

export interface ThreadNoteProps {
  threadKey: string;
  note: ThreadNoteView | null;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  /** The key that opens the editor here, shown on the button. */
  shortcut?: string;
  className?: string;
}

/** The note box (or, while editing, the editor). Nothing renders without a note unless editing. */
export function ThreadNote({
  threadKey,
  note,
  editing,
  onEditingChange,
  shortcut,
  className,
}: ThreadNoteProps) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const save = async (text: string) => {
    setBusy(true);
    const ok = await saveThreadNote(queryClient, threadKey, text, note?.text ?? null);
    setBusy(false);
    if (ok) onEditingChange(false);
  };
  if (!note && !editing) return null;
  return (
    <section
      aria-label="Your note"
      data-testid="thread-note"
      className={cn("rounded-lg border border-note-line bg-note/70 px-3 py-2", className)}
    >
      <p className="mm-kicker flex items-center gap-1.5 !text-note-ink">
        <NotebookPen aria-hidden className="size-3.5" />
        Your note
      </p>
      {editing ? (
        <div className="mt-1.5">
          <NoteEditor
            initial={note?.text ?? ""}
            busy={busy}
            onSave={(text) => void save(text)}
            onCancel={() => onEditingChange(false)}
          />
        </div>
      ) : (
        <div className="mt-0.5 flex flex-wrap items-start justify-between gap-2">
          <p className="min-w-0 text-[15px] break-words whitespace-pre-wrap text-ink">
            {note!.text}
          </p>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => onEditingChange(true)}>
              Edit
              {shortcut ? <Kbd>{shortcut}</Kbd> : null}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void save("")}>
              Remove
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
